import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { filter, tap, catchError } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { throwError } from 'rxjs';

const ANALYTICS_CONFIG = new InjectionToken('psu.utils.analytics.config');

class GoogleAnalyticsService {
    constructor(
    // This has to be here since @angular/common calls it an Object
    // eslint-disable-next-line @typescript-eslint/ban-types
    platform, config) {
        this.platform = platform;
        this.config = config;
        this.scriptLoaded = false;
        if (config && config.trackingId && config.tagManagerContainerId) {
            this.gtag = this.loadGoogleAnalytics();
            this.gtag('config', this.config.trackingId, {
                send_page_view: false,
            });
        }
        else {
            console.warn('Google Analytics Config not found, analytics are not being collected');
        }
    }
    pageView(url) {
        if (this.config && this.config.trackingId && this.config.tagManagerContainerId) {
            this.gtag('config', this.config.trackingId, {
                page_path: url,
            });
        }
    }
    recordEvent(action, category, label) {
        this.gtag('event', action, {
            event_category: category,
            event_label: label,
        });
    }
    recordException(exceptionMsg) {
        this.gtag('event', 'exception', {
            method: 'Angular',
            event_category: 'exception',
            event_label: 'Network Error in From User',
            description: exceptionMsg,
        });
    }
    getReady() {
        if (!this.scriptLoaded) {
            this.scriptLoaded = true;
            if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || [];
                // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
                window.gtag = function () {
                    // https://developers.google.com/tag-platform/tag-manager/web/datalayer
                    window.dataLayer.push(arguments);
                };
                /* This refactor will behave differently than the function above
                // window.gtag = (...args): void => {
                //   window.dataLayer.push(args);
                // };
                */
                window.gtag('js', new Date());
            }
            const doc = document.body;
            const script = document.createElement('script');
            script.innerHTML = '';
            script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.trackingId}`;
            script.async = true;
            script.defer = true;
            doc.appendChild(script);
        }
    }
    loadGoogleAnalytics() {
        let ret;
        if (isPlatformBrowser(this.platform)) {
            this.getReady();
            ret = window.gtag;
            if (!ret) {
                console.warn('Unable to find Google Analytics function (window.gtag).  Returning empty function.');
                ret = () => { };
            }
        }
        else {
            ret = () => { };
        }
        return ret;
    }
}
GoogleAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleAnalyticsService, deps: [{ token: PLATFORM_ID }, { token: ANALYTICS_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleAnalyticsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ANALYTICS_CONFIG]
                }] }]; } });

class AnalyticsService {
    constructor(router, google) {
        this.router = router;
        this.google = google;
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), tap((event) => this.google.pageView(event.urlAfterRedirects)))
            .subscribe((event) => { });
    }
    recordEvent(action, category, label) {
        this.google.recordEvent(action, category, label);
    }
    recordError(exceptionMsg) {
        this.google.recordException(exceptionMsg);
    }
}
AnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnalyticsService, deps: [{ token: i1.Router }, { token: GoogleAnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable });
AnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: GoogleAnalyticsService }]; } });

class GoogleTagManagerService {
    constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    platform, config) {
        this.platform = platform;
        this.config = config;
        this.scriptLoaded = false;
        this.loadGoogleTagManager();
    }
    loadGoogleTagManager() {
        if (isPlatformBrowser(this.platform)) {
            if (!this.scriptLoaded) {
                if (this.config.tagManagerContainerId) {
                    this.scriptLoaded = true;
                    try {
                        const head = document.head;
                        const script = document.createElement('script');
                        script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${this.config.tagManagerContainerId}');`;
                        head.appendChild(script);
                        const doc = document.body;
                        const noscript = document.createElement('noscript');
                        noscript.innerHTML =
                            `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.config.tagManagerContainerId}"` +
                                'height="0" width="0" style="display:none;visibility:hidden"></iframe>';
                        doc.appendChild(script);
                    }
                    catch (err) {
                        this.scriptLoaded = false;
                        console.error(`Error loading GoogleTagManager scripts: ${err}`);
                    }
                }
                else {
                    console.error(`You have injected the GoogleTagManagerService but it did not find tagManagerContainerId in
             the ANALYTICS_CONFIG InjectionToken.  Possible race condition?`);
                }
            }
        }
    }
}
GoogleTagManagerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleTagManagerService, deps: [{ token: PLATFORM_ID }, { token: ANALYTICS_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleTagManagerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleTagManagerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GoogleTagManagerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ANALYTICS_CONFIG]
                }] }]; } });

const X_REQUEST_ID = 'x-request-id';
class ErrorStatusInterceptor {
    constructor(aService) {
        this.aService = aService;
    }
    intercept(request, next) {
        return next.handle(request).pipe(catchError((error) => {
            let errorMsg = 'not provided';
            // capture the request ID either from the error response or the original request
            let xRequestId = '';
            if (error.headers.has(X_REQUEST_ID)) {
                xRequestId = error.headers.get(X_REQUEST_ID) ?? 'xrequestid not supplied';
            }
            else if (request.headers.has(X_REQUEST_ID)) {
                xRequestId = request.headers.get(X_REQUEST_ID) ?? 'xrequestid not supplied';
            }
            if (error.error && error.error.errorMessage) {
                errorMsg = error.error.errorMessage;
            }
            // there are 2 possible types of errors: client side and server side HTTP errors
            // CORS errors fall into client side errors, they will have ErrorCode 0 in chrome and you will not see the real
            // response code.
            if (error.error instanceof ErrorEvent) {
                // client error
                errorMsg = `Client/Network Error: ${error.error.type} -> ${error.error.message}`;
            }
            else {
                // server error, pull out HTTP response status and message
                errorMsg = `HTTP ${error.status}: ${error.statusText}, message: [${errorMsg}]`;
            }
            this.aService.recordError(`${errorMsg} time: [${new Date().toISOString()}] from [${error.url}] requestMethod: ${request.method} fullRequestUrl: ${request.urlWithParams} ${!!xRequestId ? `xRequestId: ${xRequestId}` : ''}`);
            return throwError(error);
        }));
    }
}
ErrorStatusInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ErrorStatusInterceptor, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorStatusInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ErrorStatusInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ErrorStatusInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: AnalyticsService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { ANALYTICS_CONFIG, AnalyticsService, ErrorStatusInterceptor, GoogleAnalyticsService, GoogleTagManagerService };
